import React, { useState, useEffect, useMemo } from "react";
import { Collapse, Tag, Typography } from "antd";
import {
  getPatientPackagesApi,
  viewIpBedApi,
} from "../../../../services/receptionist.services";
import { formatToINR } from "../../../../helpers/utility";
import PatientPackageTable from "./PatientPackageTable";
import useQuery from "../../../../hooks/useQuery";
import dayjs from "dayjs";
import styles from "./PackagesIp.module.css";

const getPackageKey = (item, index) =>
  item?.id ?? item?.package_id ?? `package-${index}`;

const formatBedPrice = (price) => {
  if (price == null || price === "") return null;
  return `${formatToINR(price)}/day`;
};

const getPackageBedInfo = (item, bedHistory, patientData) => {
  const bd = item?.bed_details || {};

  let roomNo = bd.room_no || bd.room_number;
  let bedType = bd.bed_type || bd.room_type || bd.room_type_name;
  let bedPrice = bd.bed_price ?? bd.price ?? bd.cash_price;

  if (!bedType) bedType = item?.package?.room_type_name;
  if (bedPrice == null || bedPrice === "") {
    bedPrice = item?.package?.room_price;
  }

  if (bedHistory?.length) {
    const pkgStart = dayjs(item?.start_date);

    const matchedBed =
      bedHistory.find((record) => {
        const bedStart = dayjs(record?.start_date);
        const bedEnd = record?.end_date ? dayjs(record.end_date) : null;
        const startsAfterBed =
          pkgStart.isSame(bedStart, "minute") || pkgStart.isAfter(bedStart);
        const startsBeforeBedEnd = !bedEnd || pkgStart.isBefore(bedEnd);
        return startsAfterBed && startsBeforeBedEnd;
      }) ||
      bedHistory.find(
        (record) =>
          record?.bed?.id === patientData?.admission?.bed?.id &&
          !record?.end_date
      );

    if (matchedBed?.bed) {
      roomNo = roomNo || matchedBed.bed.room_no;
      bedType = bedType || matchedBed.bed.room_type;
      if (bedPrice == null || bedPrice === "") {
        bedPrice = matchedBed.bed.cash_price;
      }
    }
  }

  if (item?.is_active && patientData?.admission?.bed) {
    const currentBed = patientData.admission.bed;
    roomNo = roomNo || currentBed.room_no;
    bedType = bedType || currentBed.room_type;
    if (bedPrice == null || bedPrice === "") {
      bedPrice = currentBed.cash_price;
    }
  }

  return { roomNo, bedType, bedPrice };
};

const PackageDetailRow = ({ label, value, valueClassName, strongLabel }) => (
  <div className={styles.packageDetailRow}>
    <span
      className={
        strongLabel ? styles.packageDetailLabelStrong : styles.packageDetailLabel
      }
    >
      {label}
    </span>
    <span className={valueClassName || styles.packageDetailValue}>
      {value ?? <span className={styles.packageDetailEmpty}>—</span>}
    </span>
  </div>
);

const PackagePanelHeader = ({ item, bedInfo, packagePriceLabel }) => {
  const metaItems = [
    bedInfo.roomNo && {
      label: "Room No:",
      value: bedInfo.roomNo,
      isPrice: false,
    },
    bedInfo.bedType && {
      label: "Type:",
      value: bedInfo.bedType,
      isPrice: false,
    },
    packagePriceLabel && {
      label: "Package Price:",
      value: packagePriceLabel,
      isPrice: true,
    },
  ].filter(Boolean);

  return (
    <div className={styles.packageHeader}>
      <Typography.Text strong className={styles.packageHeaderTitle}>
        {item?.package?.name}
      </Typography.Text>

      <Tag className={styles.packageDateTag}>
        {dayjs(item?.start_date).format("MMM DD")}
        {item?.end_date && ` – ${dayjs(item?.end_date).format("MMM DD")}`}
      </Tag>

      {item?.is_active && (
        <Tag className={styles.packageActiveTag}>Active</Tag>
      )}

      {metaItems.length > 0 && (
        <div className={styles.packageHeaderMeta}>
          {metaItems.map((meta, metaIndex) => (
            <React.Fragment key={meta.label}>
              {metaIndex > 0 && <span className={styles.packageMetaDivider} />}
              <span className={styles.packageMetaItem}>
                <span className={styles.packageMetaLabel}>{meta.label}</span>
                <span
                  className={
                    meta.isPrice
                      ? styles.packageMetaValuePrice
                      : styles.packageMetaValue
                  }
                >
                  {meta.value}
                </span>
              </span>
            </React.Fragment>
          ))}
        </div>
      )}
    </div>
  );
};

const PatientPackageDetails = ({ refresh, patientData }) => {
  const params = useQuery();
  const [packages, setPackages] = useState([]);
  const [bedHistory, setBedHistory] = useState([]);
  const [activeKey, setActiveKey] = useState(undefined);

  useEffect(() => {
    getPatientPackagesApi(params?.patient)
      .then((res) => setPackages(res?.data?.data))
      .catch((err) => console.log(err));
  }, [refresh, params?.patient]);

  useEffect(() => {
    viewIpBedApi(params?.patient)
      .then((res) => setBedHistory(res?.data?.data || []))
      .catch((err) => console.log(err));
  }, [refresh, params?.patient]);

  const sortedPackages = useMemo(() => {
    if (!packages?.length) return [];
    return [...packages].sort(
      (a, b) => dayjs(b?.start_date).valueOf() - dayjs(a?.start_date).valueOf()
    );
  }, [packages]);

  useEffect(() => {
    if (!sortedPackages.length) {
      setActiveKey(undefined);
      return;
    }
    setActiveKey(getPackageKey(sortedPackages[0], 0));
  }, [sortedPackages, refresh]);

  if (!sortedPackages.length) return null;

  return (
    <Collapse
      accordion
      expandIconPosition="right"
      activeKey={activeKey}
      onChange={(key) => setActiveKey(key)}
      className={`custom-collapse ${styles.packageDetailsCollapse}`}
      bordered={false}
    >
      {sortedPackages.map((item, index) => {
        const bedInfo = getPackageBedInfo(item, bedHistory, patientData);
        const bedPriceLabel = formatBedPrice(bedInfo.bedPrice);
        const packagePriceLabel =
          item?.total_package_price != null && item?.total_package_price !== ""
            ? formatToINR(item.total_package_price)
            : null;
        const actualPackagePrice =
          Number(bedInfo.bedPrice || 0) +
          Number(item?.actual_package_price || 0);
        const discountAmount = Number(item?.discount || 0);

        return (
          <Collapse.Panel
            key={getPackageKey(item, index)}
            className="white-panel-header"
            header={
              <PackagePanelHeader
                item={item}
                bedInfo={bedInfo}
                packagePriceLabel={packagePriceLabel}
              />
            }
          >
            <Typography.Text strong className={styles.packageSectionTitle}>
              Services
            </Typography.Text>

            <PatientPackageTable data={item} />

            <Typography.Text strong className={styles.packageSectionTitleSpaced}>
              Bed Allotted
            </Typography.Text>

            <div className={styles.packageBedBox}>
              <PackageDetailRow label="Room No:" value={bedInfo.roomNo} />
              <PackageDetailRow label="Bed Type:" value={bedInfo.bedType} />
              <PackageDetailRow
                label="Bed Price:"
                value={bedPriceLabel}
                valueClassName={styles.packageDetailValueOrange}
              />
            </div>

            <div className={styles.packagePricingRows}>
              <PackageDetailRow
                label="Actual Package Price"
                value={formatToINR(actualPackagePrice)}
                valueClassName={styles.packageDetailValueOrange}
                strongLabel
              />
              <PackageDetailRow
                label="Discount"
                value={
                  discountAmount > 0
                    ? `-${formatToINR(discountAmount)}`
                    : formatToINR(0)
                }
                valueClassName={styles.packageDetailValueGreen}
                strongLabel
              />
            </div>

            <div className={styles.packageTotalBar}>
              <span className={styles.packageTotalLabel}>
                Total Package Price
              </span>
              <span className={styles.packageTotalValue}>
                {formatToINR(item?.total_package_price)}
              </span>
            </div>
          </Collapse.Panel>
        );
      })}
    </Collapse>
  );
};

export default PatientPackageDetails;
